import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  FileText,
  DollarSign,
  GraduationCap,
  LifeBuoy,
  Settings,
  Plus,
  Search,
  CheckCircle2,
  Clock,
  AlertCircle,
  TrendingUp,
  Award,
  ChevronRight,
  Filter,
  Shield,
  Edit,
  Eye,
  Trash2,
  Lock,
  ArrowRight,
  Download,
  CreditCard,
  Building,
  MessageSquare,
  PlusCircle,
  X,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type {
  Lead,
  Service,
  Quotation,
  Project,
  Invoice,
  Course,
  SupportTicket,
  Profile,
  UserRole,
} from '../../types/database';
import Navbar from '../../components/feature/Navbar';
import { generateInvoicePDF, generateQuotationPDF } from '../../lib/pdfGenerator';
import TicketChatModal from '../../components/support/TicketChatModal';

export default function AdminDashboardPage() {
  const { user, isStaff, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [currentTab, setCurrentTab] = useState<
    'overview' | 'leads' | 'services' | 'quotes' | 'projects' | 'invoices' | 'academy' | 'support' | 'users'
  >('overview');

  // Database States
  const [leads, setLeads] = useState<Lead[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [adminError, setAdminError] = useState<string | null>(null);

  // Edit service pricing modal
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [servicePriceInput, setServicePriceInput] = useState<string>('');
  const [serviceModelInput, setServiceModelInput] = useState<string>('Fixed Price');

  // Support ticket chat modal
  const [selectedTicketForChat, setSelectedTicketForChat] = useState<SupportTicket | null>(null);
  const [showChatModal, setShowChatModal] = useState(false);

  // Create Quotation Modal
  const [showCreateQuoteModal, setShowCreateQuoteModal] = useState(false);
  const [quoteClientId, setQuoteClientId] = useState('');
  const [quoteClientName, setQuoteClientName] = useState('');
  const [quoteClientEmail, setQuoteClientEmail] = useState('');
  const [quoteValidUntil, setQuoteValidUntil] = useState('');
  const [quoteDiscount, setQuoteDiscount] = useState('0');
  const [quoteTerms, setQuoteTerms] = useState(
    '1. 50% deposit upon acceptance to commence milestones.\n2. Includes 14 days of prototype adjustments.'
  );
  const [quoteLineItems, setQuoteLineItems] = useState<
    { description: string; quantity: number; unit_price: number }[]
  >([{ description: 'Custom Web Design & System Architecture', quantity: 1, unit_price: 450 }]);
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);

  // Create Invoice Modal
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [invClientId, setInvClientId] = useState('');
  const [invDueDate, setInvDueDate] = useState('');
  const [invTax, setInvTax] = useState('0');
  const [invDiscount, setInvDiscount] = useState('0');
  const [invTerms, setInvTerms] = useState('Due within 14 days of issuance.');
  const [invLineItems, setInvLineItems] = useState<
    { description: string; quantity: number; unit_price: number }[]
  >([{ description: 'Full Platform Implementation Milestone', quantity: 1, unit_price: 350 }]);
  const [invSubmitting, setInvSubmitting] = useState(false);

  // Record Manual Payment Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [targetInvoice, setTargetInvoice] = useState<Invoice | null>(null);
  const [manualPayAmount, setManualPayAmount] = useState('');
  const [manualPayProvider, setManualPayProvider] = useState<'Mobile Money' | 'Bank Transfer' | 'Cash'>('Mobile Money');
  const [manualPayRef, setManualPayRef] = useState('');
  const [paySubmitting, setPaySubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchAllAdminData();
  }, [user]);

  const fetchAllAdminData = async () => {
    setLoading(true);
    setAdminError(null);
    try {
      const results = await Promise.allSettled([
        // 0. Leads
        supabase.from('leads').select('*, service:services(*)').order('created_at', { ascending: false }),
        // 1. Services
        supabase.from('services').select('*, category:service_categories(*)').order('display_order'),
        // 2. Quotations with items
        supabase.from('quotations').select('*, items:quotation_items(*)').order('created_at', { ascending: false }),
        // 3. Projects
        supabase.from('projects').select('*, client:profiles(*)').order('created_at', { ascending: false }),
        // 4. Invoices with items and client
        supabase.from('invoices').select('*, client:profiles(*), items:invoice_items(*)').order('created_at', { ascending: false }),
        // 5. Courses
        supabase.from('courses').select('*, category:course_categories(*)').order('display_order'),
        // 6. Support Tickets with client
        supabase.from('support_tickets').select('*, client:profiles(*)').order('created_at', { ascending: false }),
        // 7. Profiles
        supabase.from('profiles').select('*').order('created_at', { ascending: false }),
      ]);

      if (results[0].status === 'fulfilled' && (results[0].value as any)?.data) {
        setLeads((results[0].value as any).data as Lead[]);
      }
      if (results[1].status === 'fulfilled' && (results[1].value as any)?.data) {
        setServices((results[1].value as any).data as Service[]);
      }
      if (results[2].status === 'fulfilled' && (results[2].value as any)?.data) {
        setQuotations((results[2].value as any).data as Quotation[]);
      }
      if (results[3].status === 'fulfilled' && (results[3].value as any)?.data) {
        setProjects((results[3].value as any).data as Project[]);
      }
      if (results[4].status === 'fulfilled' && (results[4].value as any)?.data) {
        setInvoices((results[4].value as any).data as Invoice[]);
      }
      if (results[5].status === 'fulfilled' && (results[5].value as any)?.data) {
        setCourses((results[5].value as any).data as Course[]);
      }
      if (results[6].status === 'fulfilled' && (results[6].value as any)?.data) {
        setTickets((results[6].value as any).data as SupportTicket[]);
      }
      if (results[7].status === 'fulfilled' && (results[7].value as any)?.data) {
        setProfiles((results[7].value as any).data as Profile[]);
      }
    } catch (err) {
      console.error('[ADMIN] Error fetching admin data:', err);
      setAdminError('Failed to load administrative records. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId: string, newStatus: any) => {
    try {
      await supabase
        .from('leads')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', leadId);
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveServicePrice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingService) return;

    try {
      const newPrice = parseFloat(servicePriceInput);
      await supabase
        .from('services')
        .update({
          base_price: newPrice,
          pricing_model: serviceModelInput as any,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingService.id);

      setServices((prev) =>
        prev.map((s) =>
          s.id === editingService.id
            ? { ...s, base_price: newPrice, pricing_model: serviceModelInput as any }
            : s
        )
      );
      setEditingService(null);
    } catch (err) {
      console.error(err);
    }
  };

  const toggleServiceStatus = async (serviceId: string, currentStatus: boolean) => {
    try {
      await supabase
        .from('services')
        .update({ is_active: !currentStatus, updated_at: new Date().toISOString() })
        .eq('id', serviceId);
      setServices((prev) =>
        prev.map((s) => (s.id === serviceId ? { ...s, is_active: !currentStatus } : s))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const convertQuoteToProject = async (quote: Quotation) => {
    try {
      const projNum = `PRJ-${Date.now().toString().slice(-6)}`;
      const { data, error } = await supabase
        .from('projects')
        .insert({
          project_number: projNum,
          title: `Project: ${quote.client_name} - ${quote.quotation_number}`,
          client_id: quote.client_id || user!.id,
          quotation_id: quote.id,
          budget: quote.total_amount,
          status: 'In Progress',
          progress_percent: 10,
          description: `Commissioned from Quotation ${quote.quotation_number}.`,
        })
        .select()
        .single();

      if (!error && data) {
        await supabase.from('quotations').update({ status: 'Accepted' }).eq('id', quote.id);
        await fetchAllAdminData();
        setCurrentTab('projects');
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Create Quotation Handler
  const handleCreateQuotation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quoteClientName || !quoteClientEmail) return;

    setQuoteSubmitting(true);
    try {
      const subtotal = quoteLineItems.reduce(
        (sum, item) => sum + item.quantity * item.unit_price,
        0
      );
      const discount = parseFloat(quoteDiscount) || 0;
      const totalAmount = Math.max(0, subtotal - discount);
      const quoteNum = `QT-${Date.now().toString().slice(-6)}`;

      const { data: qData, error: qErr } = await supabase
        .from('quotations')
        .insert({
          quotation_number: quoteNum,
          client_id: quoteClientId || null,
          client_name: quoteClientName,
          client_email: quoteClientEmail,
          subtotal,
          discount,
          tax: 0,
          total_amount: totalAmount,
          currency: 'USD',
          status: 'Sent',
          valid_until: quoteValidUntil || null,
          terms: quoteTerms,
          created_by: user?.id,
        })
        .select()
        .single();

      if (qErr) throw qErr;

      // Insert quotation items
      if (qData) {
        const itemsToInsert = quoteLineItems.map((item) => ({
          quotation_id: qData.id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.quantity * item.unit_price,
        }));
        await supabase.from('quotation_items').insert(itemsToInsert);
      }

      setShowCreateQuoteModal(false);
      await fetchAllAdminData();
    } catch (err) {
      console.error('Error creating quotation:', err);
    } finally {
      setQuoteSubmitting(false);
    }
  };

  // Create Invoice Handler
  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invClientId || !invDueDate) return;

    setInvSubmitting(true);
    try {
      const subtotal = invLineItems.reduce(
        (sum, item) => sum + item.quantity * item.unit_price,
        0
      );
      const tax = parseFloat(invTax) || 0;
      const discount = parseFloat(invDiscount) || 0;
      const totalAmount = Math.max(0, subtotal + tax - discount);
      const invNum = `INV-${Date.now().toString().slice(-6)}`;

      const { data: invData, error: invErr } = await supabase
        .from('invoices')
        .insert({
          invoice_number: invNum,
          client_id: invClientId,
          subtotal,
          tax,
          discount,
          total_amount: totalAmount,
          amount_paid: 0,
          balance_due: totalAmount,
          currency: 'USD',
          status: 'Issued',
          due_date: invDueDate,
          terms: invTerms,
        })
        .select()
        .single();

      if (invErr) throw invErr;

      if (invData) {
        const itemsToInsert = invLineItems.map((item) => ({
          invoice_id: invData.id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.quantity * item.unit_price,
        }));
        await supabase.from('invoice_items').insert(itemsToInsert);
      }

      setShowCreateInvoiceModal(false);
      await fetchAllAdminData();
    } catch (err) {
      console.error('Error creating invoice:', err);
    } finally {
      setInvSubmitting(false);
    }
  };

  // Record Manual Payment Handler
  const handleRecordManualPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetInvoice || !manualPayAmount) return;

    setPaySubmitting(true);
    try {
      const amount = parseFloat(manualPayAmount);
      const paymentRef = manualPayRef || `PAY-REC-${Date.now().toString().slice(-6)}`;

      await supabase.from('payments').insert({
        payment_reference: paymentRef,
        invoice_id: targetInvoice.id,
        client_id: targetInvoice.client_id,
        amount,
        currency: targetInvoice.currency || 'USD',
        provider: manualPayProvider,
        status: 'Paid',
        verified_at: new Date().toISOString(),
        payment_method: `${manualPayProvider} (Admin Recorded)`,
      });

      const newPaid = Number(targetInvoice.amount_paid || 0) + amount;
      const newBalance = Math.max(0, targetInvoice.total_amount - newPaid);
      const newStatus = newBalance <= 0 ? 'Paid' : 'Partially Paid';

      await supabase
        .from('invoices')
        .update({
          amount_paid: newPaid,
          balance_due: newBalance,
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', targetInvoice.id);

      setShowPaymentModal(false);
      setTargetInvoice(null);
      await fetchAllAdminData();
    } catch (err) {
      console.error('Error recording payment:', err);
    } finally {
      setPaySubmitting(false);
    }
  };

  const totalInvoiced = invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const totalCollected = invoices.reduce((sum, inv) => sum + (inv.amount_paid || 0), 0);
  const activeProjectsCount = projects.filter((p) => p.status === 'In Progress').length;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col pt-20">
      <Navbar />

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 bg-darkBlue-900 border-r border-darkBlue-800 hidden lg:flex flex-col justify-between py-6">
          <div className="px-4 space-y-6">
            <div className="px-2">
              <span className="text-[10px] uppercase tracking-widest text-clairBlue-400 font-bold">
                Operations Center
              </span>
              <h2 className="text-white font-bold text-lg tracking-tight">Admin Console</h2>
            </div>

            <nav className="space-y-1">
              {[
                { id: 'overview', label: 'Dashboard Overview', icon: LayoutDashboard },
                { id: 'leads', label: 'CRM & Leads', icon: Users, badge: leads.filter((l) => l.status === 'New Lead').length },
                { id: 'services', label: 'Services Catalog', icon: Settings },
                { id: 'quotes', label: 'Quotations', icon: FileText, badge: quotations.filter((q) => q.status === 'Sent').length },
                { id: 'projects', label: 'Projects & Work', icon: Briefcase, badge: activeProjectsCount },
                { id: 'invoices', label: 'Billing & Invoices', icon: DollarSign },
                { id: 'payments', label: 'Verify Payments', icon: CreditCard, path: '/admin/payments' },
                { id: 'payment-channels', label: 'Payment Channels', icon: Building, path: '/admin/payment-instructions' },
                { id: 'academy', label: 'Academy LMS', icon: GraduationCap },
                { id: 'support', label: 'Support Desk', icon: LifeBuoy, badge: tickets.filter((t) => t.status === 'Open').length },
                { id: 'users', label: 'Users & Roles', icon: Shield },
              ].map((item) => (
                item.path ? (
                  <Link
                    key={item.id}
                    to={item.path}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-all text-gray-300 hover:bg-white/5 hover:text-white"
                  >
                    <div className="flex items-center gap-2.5">
                      <item.icon className="w-4 h-4 text-clairBlue-300" />
                      <span>{item.label}</span>
                    </div>
                  </Link>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => setCurrentTab(item.id as any)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                      currentTab === item.id
                        ? 'bg-clairBlue-600 text-white shadow-md'
                        : 'text-gray-300 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <item.icon className="w-4 h-4 text-clairBlue-300" />
                      <span>{item.label}</span>
                    </div>
                    {item.badge ? (
                      <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-white">
                        {item.badge}
                      </span>
                    ) : null}
                  </button>
                )
              ))}
            </nav>
          </div>

          <div className="px-6 pt-4 border-t border-white/10 text-xs text-gray-400">
            <p className="font-semibold text-white">Bienvenu Masengesho</p>
            <p className="text-[11px] text-clairBlue-400">Super Administrator</p>
          </div>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 p-6 sm:p-10 overflow-y-auto">
          {/* Mobile Tab Selector */}
          <div className="lg:hidden mb-6 flex gap-2 overflow-x-auto pb-2 scrollbar-none">
            {['overview', 'leads', 'services', 'quotes', 'projects', 'invoices', 'academy', 'support', 'users'].map(
              (tab) => (
                <button
                  key={tab}
                  onClick={() => setCurrentTab(tab as any)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap capitalize ${
                    currentTab === tab ? 'bg-darkBlue-600 text-white' : 'bg-white text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              )
            )}
          </div>

          {loading ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 border-4 border-darkBlue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-xs text-gray-500 font-medium">Loading administrative records...</p>
            </div>
          ) : adminError ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-red-100 p-12 max-w-lg mx-auto">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Unable to Load Admin Records</h3>
              <p className="text-xs text-gray-500 mb-6">{adminError}</p>
              <button
                onClick={fetchAllAdminData}
                className="px-6 py-2.5 bg-darkBlue-600 hover:bg-darkBlue-700 text-white rounded-xl text-xs font-bold transition-colors"
              >
                Retry Refresh
              </button>
            </div>
          ) : currentTab === 'overview' ? (
            <div className="space-y-8">
              {/* Top Banner */}
              <div className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-black text-gray-900">
                    Business Operations Overview
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">
                    Real-time metrics from Supabase PostgreSQL database
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={fetchAllAdminData}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors"
                  >
                    Refresh Sync
                  </button>
                </div>
              </div>

              {/* KPI Stat Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-2xl border border-gray-200/70 shadow-sm">
                  <div className="flex items-center justify-between text-gray-500 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider">Total Leads</span>
                    <Users className="w-5 h-5 text-clairBlue-600" />
                  </div>
                  <span className="text-3xl font-black text-gray-900">{leads.length}</span>
                  <span className="text-[11px] text-emerald-600 block mt-1 font-semibold">
                    {leads.filter((l) => l.status === 'Won').length} converted won
                  </span>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-200/70 shadow-sm">
                  <div className="flex items-center justify-between text-gray-500 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider">Invoiced Total</span>
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <span className="text-3xl font-black text-gray-900">${totalInvoiced.toFixed(2)}</span>
                  <span className="text-[11px] text-gray-400 block mt-1">
                    Across {invoices.length} invoices
                  </span>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-200/70 shadow-sm">
                  <div className="flex items-center justify-between text-gray-500 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider">Collected Revenue</span>
                    <TrendingUp className="w-5 h-5 text-clairBlue-600" />
                  </div>
                  <span className="text-3xl font-black text-gray-900">${totalCollected.toFixed(2)}</span>
                  <span className="text-[11px] text-emerald-600 block mt-1 font-semibold">
                    Verified settlements
                  </span>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-200/70 shadow-sm">
                  <div className="flex items-center justify-between text-gray-500 mb-2">
                    <span className="text-xs font-semibold uppercase tracking-wider">Active Projects</span>
                    <Briefcase className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-3xl font-black text-gray-900">{activeProjectsCount}</span>
                  <span className="text-[11px] text-gray-400 block mt-1">
                    {projects.length} lifetime projects
                  </span>
                </div>
              </div>
            </div>
          ) : currentTab === 'leads' ? (
            /* CRM & Leads Pipeline */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">CRM & Leads Pipeline</h2>
                  <p className="text-xs text-gray-500">Track and nurture incoming inquiries from contact forms and service requests</p>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-gray-200/70 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs min-w-[700px]">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wider font-semibold">
                      <tr>
                        <th className="px-6 py-4">Lead Name / Org</th>
                        <th className="px-6 py-4">Contact</th>
                        <th className="px-6 py-4">Service</th>
                        <th className="px-6 py-4">Budget / Timeline</th>
                        <th className="px-6 py-4">Pipeline Status</th>
                        <th className="px-6 py-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700 font-medium">
                      {leads.map((l) => (
                        <tr key={l.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <p className="font-bold text-darkBlue-900">{l.full_name}</p>
                            {l.organization && <p className="text-gray-400">{l.organization}</p>}
                          </td>
                          <td className="px-6 py-4">
                            <p>{l.email}</p>
                            <p className="text-gray-400">{l.phone}</p>
                          </td>
                          <td className="px-6 py-4">{l.service?.title || 'General Inquiry'}</td>
                          <td className="px-6 py-4">
                            ${l.estimated_budget || 0} • {l.timeline}
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={l.status}
                              onChange={(e) => updateLeadStatus(l.id, e.target.value)}
                              className="px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold focus:outline-none"
                            >
                              <option value="New Lead">New Lead</option>
                              <option value="Contacted">Contacted</option>
                              <option value="Qualified">Qualified</option>
                              <option value="Quotation Sent">Quotation Sent</option>
                              <option value="Negotiation">Negotiation</option>
                              <option value="Won">Won</option>
                              <option value="Lost">Lost</option>
                            </select>
                          </td>
                          <td className="px-6 py-4">
                            <button
                              onClick={() => {
                                alert(`Lead Brief / Notes:\n\n${l.notes || 'No detailed notes provided.'}`);
                              }}
                              className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs font-bold text-gray-700"
                            >
                              View Brief
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : currentTab === 'services' ? (
            /* Services Catalog */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Configurable Services Catalog</h2>
                  <p className="text-xs text-gray-500">Edit base prices, change pricing models, or activate/deactivate services in real time without modifying code</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((srv) => (
                  <div
                    key={srv.id}
                    className="bg-white rounded-3xl p-6 border border-gray-200/80 shadow-sm flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-bold text-clairBlue-600 bg-clairBlue-50 px-2.5 py-0.5 rounded-full">
                          {srv.pricing_model}
                        </span>
                        <button
                          onClick={() => toggleServiceStatus(srv.id, srv.is_active)}
                          className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            srv.is_active
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : 'bg-red-50 text-red-700 border border-red-200'
                          }`}
                        >
                          {srv.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </div>

                      <h3 className="font-bold text-sm text-gray-900 mb-1">{srv.title}</h3>
                      <p className="text-xs text-gray-500 mb-4 line-clamp-2">{srv.description}</p>
                    </div>

                    <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                      <div>
                        <span className="text-[11px] text-gray-400 block">Base Price</span>
                        <span className="text-lg font-black text-darkBlue-900">
                          ${srv.base_price.toFixed(2)} {srv.currency}
                        </span>
                      </div>

                      <button
                        onClick={() => {
                          setEditingService(srv);
                          setServicePriceInput(srv.base_price.toString());
                          setServiceModelInput(srv.pricing_model);
                        }}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
                      >
                        <Edit className="w-3.5 h-3.5" />
                        <span>Edit Pricing</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : currentTab === 'quotes' ? (
            /* Quotations Management */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Quotations & Scopes of Work</h2>
                  <p className="text-xs text-gray-500">Create, dispatch, and convert client quotations into active projects</p>
                </div>
                <button
                  onClick={() => setShowCreateQuoteModal(true)}
                  className="px-4 py-2.5 bg-darkBlue-900 hover:bg-darkBlue-800 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Issue Formal Quote</span>
                </button>
              </div>

              <div className="bg-white rounded-3xl border border-gray-200/70 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs min-w-[650px]">
                  <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wider font-semibold">
                    <tr>
                      <th className="px-6 py-4">Quote #</th>
                      <th className="px-6 py-4">Client</th>
                      <th className="px-6 py-4">Total Amount</th>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700 font-medium">
                    {quotations.map((q) => (
                      <tr key={q.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-mono font-bold text-darkBlue-900">
                          {q.quotation_number}
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-bold">{q.client_name}</p>
                          <p className="text-gray-400">{q.client_email}</p>
                        </td>
                        <td className="px-6 py-4 font-bold text-gray-900">
                          ${q.total_amount.toFixed(2)} {q.currency}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                              q.status === 'Accepted'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}
                          >
                            {q.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => generateQuotationPDF(q, q.items || [], q.client)}
                              className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                              title="Download PDF"
                            >
                              <Download className="w-3.5 h-3.5" />
                              <span>PDF</span>
                            </button>

                            {q.status !== 'Accepted' && (
                              <button
                                onClick={() => convertQuoteToProject(q)}
                                className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors"
                              >
                                Convert to Project
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          ) : currentTab === 'projects' ? (
            /* Projects Management */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Projects & Workspaces</h2>
                  <p className="text-xs text-gray-500">Manage client deliverables, update progress, and coordinate milestones</p>
                </div>
              </div>

              <div className="space-y-4">
                {projects.map((proj) => (
                  <div key={proj.id} className="bg-white rounded-3xl p-6 border border-gray-200/70 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="font-mono text-xs font-bold text-gray-400 mr-2">
                          {proj.project_number}
                        </span>
                        <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-clairBlue-50 text-clairBlue-700 border border-clairBlue-200">
                          {proj.status}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-darkBlue-900">
                        ${proj.budget.toFixed(2)} {proj.currency}
                      </span>
                    </div>

                    <h3 className="font-bold text-base text-gray-900 mb-1">{proj.title}</h3>
                    <p className="text-xs text-gray-500 mb-4">{proj.description}</p>

                    <div className="flex items-center gap-4 text-xs font-semibold text-gray-500">
                      <span>Progress: {proj.progress_percent}%</span>
                      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-emerald-500 rounded-full"
                          style={{ width: `${proj.progress_percent}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : currentTab === 'invoices' ? (
            /* Invoices Management */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Invoices & Financial Management</h2>
                  <p className="text-xs text-gray-500">Track paid revenue, pending invoices, and verified receipts</p>
                </div>
                <button
                  onClick={() => setShowCreateInvoiceModal(true)}
                  className="px-4 py-2.5 bg-darkBlue-900 hover:bg-darkBlue-800 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Issue New Invoice</span>
                </button>
              </div>

              <div className="bg-white rounded-3xl border border-gray-200/70 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs min-w-[700px]">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wider font-semibold">
                      <tr>
                        <th className="px-6 py-4">Invoice #</th>
                        <th className="px-6 py-4">Client</th>
                        <th className="px-6 py-4">Due Date</th>
                        <th className="px-6 py-4">Total</th>
                        <th className="px-6 py-4">Balance Due</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700 font-medium">
                      {invoices.map((inv) => {
                        const balDue = Number(
                          inv.balance_due ?? (inv.total_amount - (inv.amount_paid || 0))
                        );

                        return (
                          <tr key={inv.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 font-mono font-bold text-darkBlue-900">{inv.invoice_number}</td>
                            <td className="px-6 py-4">{inv.client?.full_name || 'Client'}</td>
                            <td className="px-6 py-4">{new Date(inv.due_date).toLocaleDateString()}</td>
                            <td className="px-6 py-4 font-bold text-gray-900">${inv.total_amount.toFixed(2)}</td>
                            <td className="px-6 py-4 font-bold text-red-600">${balDue.toFixed(2)}</td>
                            <td className="px-6 py-4">
                              <span
                                className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                                  inv.status === 'Paid'
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : inv.status === 'Partially Paid'
                                    ? 'bg-amber-50 text-amber-700'
                                    : 'bg-red-50 text-red-700'
                                }`}
                              >
                                {inv.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => generateInvoicePDF(inv, inv.items || [], inv.client)}
                                  className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors"
                                  title="Download PDF Invoice"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  <span>PDF</span>
                                </button>

                                {balDue > 0 && (
                                  <button
                                    onClick={() => {
                                      setTargetInvoice(inv);
                                      setManualPayAmount(balDue.toString());
                                      setShowPaymentModal(true);
                                    }}
                                    className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors"
                                  >
                                    Record Payment
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : currentTab === 'academy' ? (
            /* Academy LMS Management */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Academy Courses & Certificates</h2>
                  <p className="text-xs text-gray-500">Manage courses, review publish requests, and inspect curriculum</p>
                </div>
                <Link
                  to="/academy"
                  className="px-4 py-2 bg-darkBlue-600 text-white rounded-xl text-xs font-bold"
                >
                  View Public Catalog
                </Link>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {courses.map((crs) => (
                  <div key={crs.id} className="bg-white rounded-3xl p-6 border border-gray-200/70 shadow-sm flex flex-col justify-between">
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full inline-block mb-2">
                        {crs.status}
                      </span>
                      <h4 className="font-bold text-sm text-gray-900 mb-1">{crs.title}</h4>
                      <p className="text-xs text-gray-500 line-clamp-2">{crs.short_description}</p>
                    </div>

                    <div className="pt-4 border-t border-gray-100 flex items-center justify-between text-xs mt-4">
                      <span className="font-bold text-darkBlue-900">${crs.price.toFixed(2)} USD</span>
                      <Link
                        to={`/academy/courses/${crs.slug}`}
                        className="text-clairBlue-600 font-bold hover:underline"
                      >
                        Preview Course →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : currentTab === 'support' ? (
            /* Support Desk Management */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Customer Support Desk</h2>
                  <p className="text-xs text-gray-500">Manage client tickets, chat in real-time, and log internal team notes</p>
                </div>
              </div>

              <div className="space-y-4">
                {tickets.map((tck) => (
                  <div
                    key={tck.id}
                    onClick={() => {
                      setSelectedTicketForChat(tck);
                      setShowChatModal(true);
                    }}
                    className="bg-white rounded-3xl p-6 border border-gray-200/70 shadow-sm cursor-pointer hover:border-clairBlue-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-gray-400">{tck.ticket_number}</span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                            tck.status === 'Resolved' || tck.status === 'Closed'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : tck.status === 'Open'
                              ? 'bg-blue-50 text-blue-700 border border-blue-200'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          {tck.status}
                        </span>
                        <span className="text-xs font-semibold text-red-500">{tck.priority} Priority</span>
                      </div>
                      <span className="text-xs text-gray-400">{new Date(tck.created_at).toLocaleDateString()}</span>
                    </div>

                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <h4 className="font-bold text-sm text-gray-900 mb-1">{tck.subject}</h4>
                        <p className="text-xs text-gray-600 line-clamp-2">{tck.description}</p>
                        {tck.client && (
                          <p className="text-[11px] text-gray-400 mt-2">
                            Client: <strong>{tck.client.full_name}</strong> ({tck.client.email})
                          </p>
                        )}
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTicketForChat(tck);
                          setShowChatModal(true);
                        }}
                        className="px-4 py-2 bg-darkBlue-900 hover:bg-darkBlue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm flex-shrink-0"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span>Open Thread</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Users & Roles Management */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Registered Users & Client Accounts</h2>
                  <p className="text-xs text-gray-500">Profiles stored securely in Supabase PostgreSQL</p>
                </div>
              </div>

              <div className="bg-white rounded-3xl border border-gray-200/70 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs min-w-[600px]">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wider font-semibold">
                      <tr>
                        <th className="px-6 py-4">User</th>
                        <th className="px-6 py-4">Type</th>
                        <th className="px-6 py-4">Organization</th>
                        <th className="px-6 py-4">Joined Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700 font-medium">
                      {profiles.map((prof) => (
                        <tr key={prof.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <p className="font-bold text-darkBlue-900">{prof.full_name || 'User'}</p>
                            <p className="text-gray-400">{prof.email}</p>
                          </td>
                          <td className="px-6 py-4">{prof.client_type || 'Individual'}</td>
                          <td className="px-6 py-4">{prof.organization_name || '—'}</td>
                          <td className="px-6 py-4 text-gray-400">{new Date(prof.created_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Edit Service Modal */}
      {editingService && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Edit Service Pricing</h3>
            <p className="text-xs text-gray-500 mb-4">{editingService.title}</p>

            <form onSubmit={handleSaveServicePrice} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  Base Price (USD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={servicePriceInput}
                  onChange={(e) => setServicePriceInput(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1.5">
                  Pricing Model
                </label>
                <select
                  value={serviceModelInput}
                  onChange={(e) => setServiceModelInput(e.target.value)}
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm"
                >
                  <option value="Fixed Price">Fixed Price</option>
                  <option value="Hourly">Hourly</option>
                  <option value="Monthly Retainer">Monthly Retainer</option>
                  <option value="Subscription">Subscription</option>
                  <option value="Custom Quote">Custom Quote</option>
                  <option value="Milestone-Based">Milestone-Based</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setEditingService(null)}
                  className="px-4 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-darkBlue-600 text-white rounded-xl text-xs font-bold hover:bg-darkBlue-700"
                >
                  Save Pricing
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Quotation Modal */}
      {showCreateQuoteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-6 sm:p-8 border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
              <h3 className="text-lg font-bold text-gray-900">Issue Formal Proposal / Quotation</h3>
              <button
                onClick={() => setShowCreateQuoteModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleCreateQuotation} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Client Full Name *</label>
                  <input
                    type="text"
                    required
                    value={quoteClientName}
                    onChange={(e) => setQuoteClientName(e.target.value)}
                    placeholder="e.g. John Doe / Asante Media"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Client Email *</label>
                  <input
                    type="email"
                    required
                    value={quoteClientEmail}
                    onChange={(e) => setQuoteClientEmail(e.target.value)}
                    placeholder="e.g. client@example.com"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Valid Until Date</label>
                <input
                  type="date"
                  value={quoteValidUntil}
                  onChange={(e) => setQuoteValidUntil(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                />
              </div>

              {/* Line items builder */}
              <div className="pt-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-gray-700 uppercase tracking-wider text-[11px]">
                    Scope Deliverables & Line Items
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setQuoteLineItems((prev) => [
                        ...prev,
                        { description: 'Additional deliverable', quantity: 1, unit_price: 100 },
                      ])
                    }
                    className="text-clairBlue-600 font-bold hover:underline"
                  >
                    + Add Item
                  </button>
                </div>

                <div className="space-y-2">
                  {quoteLineItems.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        type="text"
                        required
                        value={item.description}
                        onChange={(e) => {
                          const val = e.target.value;
                          setQuoteLineItems((prev) =>
                            prev.map((it, i) => (i === idx ? { ...it, description: val } : it))
                          );
                        }}
                        placeholder="Deliverable description"
                        className="flex-1 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs"
                      />
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => {
                          const val = Number(e.target.value) || 1;
                          setQuoteLineItems((prev) =>
                            prev.map((it, i) => (i === idx ? { ...it, quantity: val } : it))
                          );
                        }}
                        className="w-14 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-center text-xs"
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => {
                          const val = Number(e.target.value) || 0;
                          setQuoteLineItems((prev) =>
                            prev.map((it, i) => (i === idx ? { ...it, unit_price: val } : it))
                          );
                        }}
                        placeholder="Rate"
                        className="w-20 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-right text-xs"
                      />
                      {quoteLineItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setQuoteLineItems((prev) => prev.filter((_, i) => i !== idx))
                          }
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Discount Amount ($ USD)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={quoteDiscount}
                  onChange={(e) => setQuoteDiscount(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Terms & Conditions</label>
                <textarea
                  rows={2}
                  value={quoteTerms}
                  onChange={(e) => setQuoteTerms(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowCreateQuoteModal(false)}
                  className="px-4 py-2 font-semibold text-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={quoteSubmitting}
                  className="px-5 py-2.5 bg-darkBlue-900 hover:bg-darkBlue-800 text-white rounded-xl font-bold"
                >
                  {quoteSubmitting ? 'Creating...' : 'Create & Dispatch Quote'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateInvoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-6 sm:p-8 border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100 mb-4">
              <h3 className="text-lg font-bold text-gray-900">Issue Official Invoice</h3>
              <button
                onClick={() => setShowCreateInvoiceModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleCreateInvoice} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">Select Client Account *</label>
                <select
                  required
                  value={invClientId}
                  onChange={(e) => setInvClientId(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                >
                  <option value="">-- Choose registered client --</option>
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.full_name} ({p.email}) {p.organization_name ? `• ${p.organization_name}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Payment Due Date *</label>
                <input
                  type="date"
                  required
                  value={invDueDate}
                  onChange={(e) => setInvDueDate(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                />
              </div>

              {/* Line items builder */}
              <div className="pt-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-gray-700 uppercase tracking-wider text-[11px]">
                    Invoice Line Items
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setInvLineItems((prev) => [
                        ...prev,
                        { description: 'Service milestone', quantity: 1, unit_price: 150 },
                      ])
                    }
                    className="text-clairBlue-600 font-bold hover:underline"
                  >
                    + Add Item
                  </button>
                </div>

                <div className="space-y-2">
                  {invLineItems.map((item, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        type="text"
                        required
                        value={item.description}
                        onChange={(e) => {
                          const val = e.target.value;
                          setInvLineItems((prev) =>
                            prev.map((it, i) => (i === idx ? { ...it, description: val } : it))
                          );
                        }}
                        placeholder="Item description"
                        className="flex-1 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs"
                      />
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => {
                          const val = Number(e.target.value) || 1;
                          setInvLineItems((prev) =>
                            prev.map((it, i) => (i === idx ? { ...it, quantity: val } : it))
                          );
                        }}
                        className="w-14 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-center text-xs"
                      />
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={item.unit_price}
                        onChange={(e) => {
                          const val = Number(e.target.value) || 0;
                          setInvLineItems((prev) =>
                            prev.map((it, i) => (i === idx ? { ...it, unit_price: val } : it))
                          );
                        }}
                        placeholder="Rate"
                        className="w-20 px-2 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-right text-xs"
                      />
                      {invLineItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setInvLineItems((prev) => prev.filter((_, i) => i !== idx))
                          }
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Taxes / Duties ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={invTax}
                    onChange={(e) => setInvTax(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Discount ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={invDiscount}
                    onChange={(e) => setInvDiscount(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowCreateInvoiceModal(false)}
                  className="px-4 py-2 font-semibold text-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={invSubmitting}
                  className="px-5 py-2.5 bg-darkBlue-900 hover:bg-darkBlue-800 text-white rounded-xl font-bold"
                >
                  {invSubmitting ? 'Issuing...' : 'Create & Issue Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Manual Payment Modal */}
      {showPaymentModal && targetInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 sm:p-8 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Record Client Payment</h3>
            <p className="text-xs text-gray-500 mb-4">
              Invoice #{targetInvoice.invoice_number} • Total: ${targetInvoice.total_amount.toFixed(2)}
            </p>

            <form onSubmit={handleRecordManualPayment} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">Amount Received ($ USD) *</label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  required
                  value={manualPayAmount}
                  onChange={(e) => setManualPayAmount(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl font-bold text-darkBlue-900 text-sm"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Payment Method *</label>
                <select
                  value={manualPayProvider}
                  onChange={(e) => setManualPayProvider(e.target.value as any)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                >
                  <option value="Mobile Money">Mobile Money (MTN / Airtel)</option>
                  <option value="Bank Transfer">Bank Wire / Stanbic / Centenary</option>
                  <option value="Cash">Cash Handover</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Reference / Transaction ID</label>
                <input
                  type="text"
                  value={manualPayRef}
                  onChange={(e) => setManualPayRef(e.target.value)}
                  placeholder="e.g. MTN-92847291 or STB-82381"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setTargetInvoice(null);
                  }}
                  className="px-4 py-2 font-semibold text-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={paySubmitting}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold"
                >
                  {paySubmitting ? 'Recording...' : 'Confirm Receipt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Real-Time Support Ticket Chat Modal */}
      <TicketChatModal
        ticket={selectedTicketForChat}
        isOpen={showChatModal}
        onClose={() => {
          setShowChatModal(false);
          setSelectedTicketForChat(null);
        }}
        onTicketUpdated={fetchAllAdminData}
      />
    </div>
  );
}
